import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SpinnerComponent } from '../../../shared/components/spinner.component';
import { PaginationComponent } from '../../../shared/components/pagination.component';
import { Router } from '@angular/router';
import { AdesoesService, Adesao } from '../adesoes.service';
import { NotificationService } from '../../../core/notification.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  standalone: true,
  selector: 'app-adesoes-list',
  imports: [CommonModule, SpinnerComponent, PaginationComponent],
  template: `
    <div class="bg-white p-6 rounded shadow">
      <div class="flex items-center justify-between mb-6">
        <h1 class="text-3xl font-serif">Adesões de Fornecedores</h1>
        <div class="text-sm text-muted">{{ meta.total || 0 }} adesões</div>
      </div>

      <!-- Filtros -->
      <div class="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label class="block text-sm font-medium mb-2">Buscar por Nome/Email</label>
          <input
            #searchInput
            type="text"
            placeholder="Nome ou email..."
            (input)="onSearch($any($event.target).value)"
            class="w-full px-3 py-2 border border-border rounded"
          />
        </div>

        <div>
          <label class="block text-sm font-medium mb-2">Status</label>
          <select (change)="onStatusChange($any($event.target).value)" class="w-full px-3 py-2 border border-border rounded">
            <option value="">Todos os status</option>
            <option value="AGUARDANDO_PAGAMENTO">Aguardando Pagamento</option>
            <option value="PENDENTE_APROVACAO">Pendente Aprovação</option>
            <option value="PAGO">Pago</option>
            <option value="ATIVO">Ativo</option>
            <option value="INATIVO">Inativo</option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium mb-2">Cidade</label>
          <input
            type="text"
            placeholder="Ex: São Paulo"
            (input)="onCityChange($any($event.target).value)"
            class="w-full px-3 py-2 border border-border rounded"
          />
        </div>

        <div>
          <label class="block text-sm font-medium mb-2">Itens por Página</label>
          <select (change)="onPageSize($any($event.target).value)" class="w-full px-3 py-2 border border-border rounded">
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
        </div>
      </div>

      <!-- Tabela de Adesões -->
      <div class="overflow-x-auto mb-6">
        <table class="w-full border-collapse">
          <thead>
            <tr class="bg-surface border-b">
              <th class="text-left p-3 text-sm font-medium">Empresa</th>
              <th class="text-left p-3 text-sm font-medium">Email</th>
              <th class="text-left p-3 text-sm font-medium">CNPJ/CPF</th>
              <th class="text-left p-3 text-sm font-medium">Instagram</th>
              <th class="text-left p-3 text-sm font-medium">Status</th>
              <th class="text-left p-3 text-sm font-medium">Data Criação</th>
              <th class="text-right p-3 text-sm font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let adesao of adesoes" class="border-b hover:bg-surface/50">
              <td class="p-3 text-sm font-medium">{{ adesao.nomeFantasia }}</td>
              <td class="p-3 text-sm text-muted">{{ adesao.email }}</td>
              <td class="p-3 text-sm">{{ adesao.cnpjCpf }}</td>
              <td class="p-3 text-sm">{{ adesao.instagramOficial || '-' }}</td>
              <td class="p-3 text-sm">
                <span [ngClass]="getStatusClass(adesao.status)" class="px-2 py-1 rounded text-xs font-medium">
                  {{ getStatusLabel(adesao.status) }}
                </span>
              </td>
              <td class="p-3 text-sm">{{ adesao.dataCriacao | date:'dd/MM/yyyy' }}</td>
              <td class="p-3 text-right">
                <button
                  (click)="verDetalhes(adesao.id)"
                  class="text-xs px-2 py-1 bg-primary text-white rounded hover:bg-primary/90 mr-1"
                >
                  Ver
                </button>
                <button
                  *ngIf="adesao.status === 'PENDENTE_APROVACAO' && adesao.fornecedorId"
                  (click)="adesao.fornecedorId && aprovar(adesao.fornecedorId)"
                  class="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 mr-1"
                >
                  Aprovar
                </button>
                <button
                  *ngIf="adesao.status === 'AGUARDANDO_PAGAMENTO'"
                  (click)="ativar(adesao.id)"
                  class="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 mr-1"
                >
                  Ativar
                </button>
                <button
                  *ngIf="adesao.status === 'ATIVO'"
                  (click)="desativar(adesao.id)"
                  class="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 mr-1"
                >
                  Desativar
                </button>
                <button
                  (click)="verAuditoria(adesao.id)"
                  class="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-surface mr-1"
                >
                  Auditoria
                </button>
                <button
                  (click)="verRelatorio(adesao.id)"
                  class="text-xs px-2 py-1 bg-purple-600 text-white rounded hover:bg-purple-700"
                >
                  Relatório
                </button>
              </td>
            </tr>
            <tr *ngIf="adesoes.length === 0 && !loading">
              <td colspan="7" class="p-8 text-center text-muted">
                <div class="text-4xl mb-2">📋</div>
                <p>Nenhuma adesão encontrada</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginação -->
      <app-pagination
        *ngIf="meta.total > 0"
        [page]="page"
        [pageSize]="pageSize"
        [total]="meta.total || 0"
        (pageChange)="onPageChange($event)"
      ></app-pagination>

      <!-- Loading -->
      <div *ngIf="loading" class="text-center py-8">
        <app-spinner></app-spinner>
      </div>
    </div>
  `
})
export class AdesoesListComponent implements OnInit, OnDestroy {
  adesoes: Adesao[] = [];
  page = 1;
  pageSize = 10;
  meta: any = {};
  loading = false;

  filters = {
    search: '',
    status: '',
    cidade: ''
  };

  private search$ = new Subject<string>();
  private subs: Subscription[] = [];

  constructor(
    private service: AdesoesService,
    private notification: NotificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.load();

    const s = this.search$
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        this.page = 1;
        this.load();
      });
    this.subs.push(s);
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  onSearch(value: string) {
    this.filters.search = value;
    this.search$.next(value);
  }

  onStatusChange(status: string) {
    this.filters.status = status;
    this.page = 1;
    this.load();
  }

  onCityChange(cidade: string) {
    this.filters.cidade = cidade;
    this.page = 1;
    this.load();
  }

  onPageSize(size: any) {
    this.pageSize = Number(size);
    this.page = 1;
    this.load();
  }

  onPageChange(page: number) {
    this.page = page;
    this.load();
  }

  load() {
    this.loading = true;
    const params: any = { page: this.page, pageSize: this.pageSize };
    
    if (this.filters.search) params.busca = this.filters.search;
    if (this.filters.status) params.status = this.filters.status;
    if (this.filters.cidade) params.cidade = this.filters.cidade;

    this.service.list(this.page, this.pageSize, params).subscribe({
      next: (res) => {
        console.log('[Adesoes] Resposta recebida:', res);
        this.adesoes = res.data || [];
        this.meta = res.meta || {};
        this.loading = false;
      },
      error: (err) => {
        console.error('[Adesoes] erro ao carregar', err);
        this.notification.error('Erro ao carregar adesões');
        this.loading = false;
      }
    });
  }

  verDetalhes(id: string) {
    this.router.navigate(['/admin/adesoes', id]);
  }

  verAuditoria(id: string) {
    this.router.navigate(['/admin/adesoes', id, 'auditoria']);
  }

  verRelatorio(id: string) {
    this.router.navigate(['/admin/adesoes', id, 'relatorio']);
  }

  aprovar(fornecedorId: string) {
    if (!confirm('Deseja aprovar o plano gratuito deste fornecedor?')) return;

    this.service.approveFreePlan(fornecedorId).subscribe({
      next: () => {
        this.notification.success('Plano gratuito aprovado com sucesso');
        this.load();
      },
      error: (err: any) => {
        console.error('[Adesoes] erro ao aprovar plano gratuito', err);
        this.notification.error(err.error?.message || 'Erro ao aprovar plano gratuito');
      }
    });
  }

  ativar(id: string) {
    if (!confirm('Deseja ativar este fornecedor?')) return;

    this.service.ativarFornecedor(id).subscribe({
      next: () => {
        this.notification.success('Fornecedor ativado com sucesso');
        this.load();
      },
      error: (err) => {
        console.error('[Adesoes] erro ao ativar', err);
        this.notification.error('Erro ao ativar fornecedor');
      }
    });
  }

  desativar(id: string) {
    if (!confirm('Deseja desativar este fornecedor?')) return;

    this.service.desativarFornecedor(id).subscribe({
      next: () => {
        this.notification.success('Fornecedor desativado com sucesso');
        this.load();
      },
      error: (err) => {
        console.error('[Adesoes] erro ao desativar', err);
        this.notification.error('Erro ao desativar fornecedor');
      }
    });
  }

  getStatusLabel(status: string): string {
    const labels: any = {
      'ATIVO': '✅ Ativo',
      'INATIVO': '❌ Inativo',
      'AGUARDANDO_PAGAMENTO': '⏳ Aguardando Pagamento',
      'PENDENTE_APROVACAO': '🕐 Pendente Aprovação',
      'PAGO': '💳 Pago'
    };
    return labels[status] || status;
  }

  getStatusClass(status: string): string {
    const classes: any = {
      'ATIVO': 'bg-green-100 text-green-800',
      'INATIVO': 'bg-red-100 text-red-800',
      'AGUARDANDO_PAGAMENTO': 'bg-yellow-100 text-yellow-800',
      'PENDENTE_APROVACAO': 'bg-orange-100 text-orange-800',
      'PAGO': 'bg-blue-100 text-blue-800'
    };
    return classes[status] || 'bg-gray-100 text-gray-800';
  }

  getBadgeClass(cor: string): string {
    const classes: any = {
      'success': 'bg-green-100 text-green-800',
      'warning': 'bg-yellow-100 text-yellow-800',
      'danger': 'bg-red-100 text-red-800',
      'info': 'bg-blue-100 text-blue-800'
    };
    return classes[cor] || 'bg-gray-100 text-gray-800';
  }
}
